import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tags, Plus, Pencil, Check, GitMerge, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";

export const TaxonomyManager = () => {
  const [nodes, setNodes] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState("");

  const load = () => api.get("/taxonomy").then((r) => setNodes(r.data.taxonomy));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    await api.post("/admin/taxonomy", { name: newName.trim(), description: "" });
    setNewName("");
    load();
    toast.success("Theme added");
  };

  const rename = async (id) => {
    if (!editName.trim()) return;
    await api.patch(`/admin/taxonomy/${id}`, { name: editName.trim() });
    setEditing(null);
    load();
    toast.success("Theme renamed");
  };

  const toggleStatus = async (n) => {
    await api.patch(`/admin/taxonomy/${n.id}`, { status: n.status === "active" ? "inactive" : "active" });
    load();
    toast.success(n.status === "active" ? "Theme deactivated" : "Theme reactivated");
  };

  const merge = async () => {
    if (!mergeSource || !mergeTarget) return;
    const res = await api.post(`/admin/taxonomy/${mergeSource}/merge`, { target_id: mergeTarget });
    toast.success(`Merged — target now covers ${res.data.merged_insight_count} statements`);
    setMergeSource(null);
    setMergeTarget("");
    load();
  };

  const active = nodes.filter((n) => n.status !== "merged");

  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-6 shadow-sm" data-testid="taxonomy-manager">
      <div className="flex items-center gap-2 mb-1">
        <Tags className="w-4 h-4 text-teal-600" />
        <h3 className="font-heading text-base font-bold text-slate-900">Clinical-Theme Taxonomy Management</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">Add, rename, merge or deactivate taxonomy themes. Merging moves all mapped statements to the target theme.</p>

      <div className="flex gap-2 mb-4 max-w-md">
        <Input data-testid="taxonomy-add-input" placeholder="New theme name…" value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} className="h-8 text-xs" />
        <Button data-testid="taxonomy-add-btn" size="sm" className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white" onClick={add}>
          <Plus className="w-3.5 h-3.5 mr-1" />Add
        </Button>
      </div>

      <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
        {nodes.map((n) => (
          <div key={n.id} className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${n.status === "merged" ? "border-slate-100 opacity-50" : n.status === "inactive" ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-100"}`} data-testid={`taxonomy-row-${n.id}`}>
            <span className="font-mono text-[10px] text-slate-400 w-14 shrink-0">{n.id}</span>
            {editing === n.id ? (
              <>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 text-xs flex-1" data-testid={`taxonomy-rename-input-${n.id}`} />
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`taxonomy-rename-save-${n.id}`} onClick={() => rename(n.id)}><Check className="w-3 h-3" /></Button>
              </>
            ) : (
              <span className="text-xs font-semibold text-slate-800 flex-1 truncate">
                {n.name}
                {n.status === "merged" && <span className="text-slate-400 font-normal ml-1.5">→ merged into {n.merged_into}</span>}
              </span>
            )}
            <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200 shrink-0">{n.insight_ids.length} stmts</Badge>
            {n.status !== "merged" && (
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" title="Rename" data-testid={`taxonomy-rename-${n.id}`}
                  onClick={() => { setEditing(n.id); setEditName(n.name); }}><Pencil className="w-3 h-3 text-slate-400" /></Button>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" title="Merge into…" data-testid={`taxonomy-merge-${n.id}`}
                  onClick={() => setMergeSource(mergeSource === n.id ? null : n.id)}><GitMerge className="w-3 h-3 text-slate-400" /></Button>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" title={n.status === "active" ? "Deactivate" : "Reactivate"} data-testid={`taxonomy-toggle-${n.id}`}
                  onClick={() => toggleStatus(n)}>
                  {n.status === "active" ? <EyeOff className="w-3 h-3 text-slate-400" /> : <Eye className="w-3 h-3 text-emerald-500" />}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {mergeSource && (
        <div className="flex items-center gap-2 mt-3 bg-indigo-50/60 border border-indigo-100 rounded-lg p-3" data-testid="taxonomy-merge-panel">
          <span className="text-xs text-slate-700">Merge <b>{nodes.find((n) => n.id === mergeSource)?.name}</b> into:</span>
          <Select value={mergeTarget} onValueChange={setMergeTarget}>
            <SelectTrigger className="h-7 text-xs w-56" data-testid="taxonomy-merge-target"><SelectValue placeholder="Select target theme" /></SelectTrigger>
            <SelectContent>
              {active.filter((n) => n.id !== mergeSource).map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="taxonomy-merge-confirm" disabled={!mergeTarget} onClick={merge}>
            Confirm merge
          </Button>
        </div>
      )}
    </div>
  );
};
